import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SkeletonLoader from '../../components/skeletonLoader/skeletonLoader';
import { FaPlus } from 'react-icons/fa';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ClearIcon from '@mui/icons-material/Clear';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';

import ProjectCard from './ProjectCard'; // Correct relative path
import Helmet from 'react-helmet';
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const ProjectDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [openProjectFormModal, setOpenProjectFormModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  // Corrected: use a ref for dragCurrentIndex instead of state, as state updates can interfere with drag events
  const dragCurrentIndex = useRef(null); 

  const [focusedImageInput, setFocusedImageInput] = useState({ index: null, field: null });

  const itemsPerPage = 3;

  const [form, setForm] = useState({
    title: '',
    description: '',
    techStack: '',
    githubUrl: '',
    liveUrl: '',
    imageUrl: [{ url: '', description: '' }],
  });

  // FIX APPLIED HERE: Ensure 'projects' is an array before filtering
  const filteredProjects = (projects || []).filter((project) => {
    const projectString = JSON.stringify(project).toLowerCase();
    return projectString.includes(searchQuery.toLowerCase());
  });

  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

  const displayedProjects = filteredProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        if (openProjectFormModal) {
          setOpenProjectFormModal(false);
          cancelEdit();
        }
        if (selectedProject) {
          setSelectedProject(null);
          setCurrentContentIndex(0); // Reset index when modal closes
        }
        if (fullScreenImage) {
          setFullScreenImage(null);
        }
        if (showDeleteConfirm) setShowDeleteConfirm(false);
      }
    };
    if (openProjectFormModal || selectedProject || fullScreenImage || showDeleteConfirm) {
      window.addEventListener('keydown', handleEscapeKey);
    }
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [openProjectFormModal, selectedProject, fullScreenImage, showDeleteConfirm]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/projects`);
      setProjects(res.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
      // It's good practice to ensure 'projects' is an array even on error
      setProjects([]); 
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      techStack: form.techStack.split(',').map((t) => t.trim()).filter(Boolean),
      imageUrl: form.imageUrl.filter(img => img.url.trim() !== ''),
    };

    try {
      if (editingId) {
        await axios.put(`${API_BASE_URL}/api/projects/${editingId}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/api/projects`, payload);
      }
      setForm({
        title: '',
        description: '',
        techStack: '',
        githubUrl: '',
        liveUrl: '',
        imageUrl: [{ url: '', description: '' }],
      });
      setEditingId(null);
      setOpenProjectFormModal(false);
      fetchProjects();
    } catch (err) {
      console.error('Error saving project:', err);
    }
  };

  const handleEdit = (project) => {
    setForm({
      title: project.title,
      description: project.description,
      techStack: project.techStack.join(', '),
      githubUrl: project.githubUrl || '',
      liveUrl: project.liveUrl || '',
      imageUrl: project.imageUrl.length > 0
        ? project.imageUrl.map(img => ({ url: img.url || '', description: img.description || '' }))
        : [{ url: '', description: '' }],
    });
    setEditingId(project._id);
    setOpenProjectFormModal(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({
      title: '',
      description: '',
      techStack: '',
      githubUrl: '',
      liveUrl: '',
      imageUrl: [{ url: '', description: '' }],
    });
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/projects/${projectToDelete._id}`);
      setShowDeleteConfirm(false);
      setProjectToDelete(null);
      fetchProjects();
    } catch (err) {
      console.error('Error deleting project:', err);
    }
  };

  const handleProjectFormClick = () => {
    if (openProjectFormModal) {
      cancelEdit();
    }
    setOpenProjectFormModal((prev) => !prev);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const totalModalSlides = selectedProject ? 1 + selectedProject.imageUrl.length : 0;

  const handlePrevContent = () => {
    setCurrentContentIndex((prevIndex) =>
      prevIndex === 0 ? totalModalSlides - 1 : prevIndex - 1
    );
  };

  const handleNextContent = () => {
    setCurrentContentIndex((prevIndex) =>
      prevIndex === totalModalSlides - 1 ? 0 : prevIndex + 1
    );
  };

  const handleFocus = (index, field) => {
    setFocusedImageInput({ index, field });
  };

  const handleBlur = (e) => {
    // A small delay to allow click event on button to register before clearing focus
    setTimeout(() => {
      const relatedTarget = e.relatedTarget;
      if (relatedTarget && (
          relatedTarget.classList.contains('image-action-btn') ||
          relatedTarget.closest('.image-url-input-row')
        )) {
        return;
      }
      setFocusedImageInput({ index: null, field: null });
    }, 100);
  };

  const handleImageUrlChange = (index, field, value) => {
    const newImageUrls = [...form.imageUrl];
    newImageUrls[index] = { ...newImageUrls[index], [field]: value };
    setForm({ ...form, imageUrl: newImageUrls });
  };

  const handleAddImageUrl = () => {
    setForm({ ...form, imageUrl: [...form.imageUrl, { url: '', description: '' }] });
  };

  const handleRemoveImageUrl = (index) => {
    const newImageUrls = form.imageUrl.filter((_, i) => i !== index);
    setForm({ ...form, imageUrl: newImageUrls.length > 0 ? newImageUrls : [{ url: '', description: '' }] });
    setFocusedImageInput({ index: null, field: null });
  };

  const handleClearFocusedInput = () => {
    if (focusedImageInput.index !== null && focusedImageInput.field !== null) {
      const { index, field } = focusedImageInput;
      const newImageUrls = [...form.imageUrl];
      newImageUrls[index] = { ...newImageUrls[index], [field]: '' };
      setForm({ ...form, imageUrl: newImageUrls });
      document.getElementById(`image-input-${index}-${field}`)?.focus();
    }
  };

  const handlePasteIntoFocusedInput = async () => {
    if (focusedImageInput.index !== null && focusedImageInput.field !== null) {
      const { index, field } = focusedImageInput;
      try {
        const text = await navigator.clipboard.readText();
        const newImageUrls = [...form.imageUrl];
        newImageUrls[index] = { ...newImageUrls[index], [field]: text };
        setForm({ ...form, imageUrl: newImageUrls });
        document.getElementById(`image-input-${index}-${field}`)?.focus();
      } catch (err) {
        console.error('Failed to read clipboard contents: ', err);
        alert('Failed to paste from clipboard. Please ensure you have granted clipboard access to your browser.');
      }
    }
  };

  const handleDragStart = (e, index) => {
    dragCurrentIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
    e.currentTarget.classList.add('dragging');
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragCurrentIndex.current !== null && dragCurrentIndex.current !== index) {
      e.currentTarget.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    const droppedIndex = parseInt(e.dataTransfer.getData("text/plain"));

    if (dragCurrentIndex.current === null || droppedIndex === targetIndex) {
        return;
    }

    const newImageUrls = [...form.imageUrl];
    const [draggedItem] = newImageUrls.splice(droppedIndex, 1);
    newImageUrls.splice(targetIndex, 0, draggedItem);

    setForm({ ...form, imageUrl: newImageUrls });
    dragCurrentIndex.current = null;
  };

  const handleDragEnd = (e) => {
    document.querySelectorAll('.image-url-input-row.dragging').forEach(el => el.classList.remove('dragging'));
    document.querySelectorAll('.image-url-input-row.drag-over').forEach(el => el.classList.remove('drag-over'));
    dragCurrentIndex.current = null;
  };

  const truncateDescription = (desc, limit = 150) =>
    desc.length > limit ? `${desc.slice(0, limit)}...` : desc;

  return (
    <div className="projects-wrapper">
      <Helmet>
        <title>NavarroJalen | Projects</title>
        <meta name="description" content="NavarroJalen | Projects" />
        <meta property="og:title" content="NavarroJalen | Projects" />
      </Helmet>
      <div className="subMenuBar">
        <button className="subMenuBarButton" onClick={handleProjectFormClick} title="Add New Project">
          <FaPlus />
        </button>

        <input
          type="text"
          className="searchbar"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="paginationButton"
            >
              Prev
            </button>
            <span className="paginationInfo">{currentPage} of {totalPages}</span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="paginationButton"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div className="dashboardSubDiv SubDiv">
        <div className="dashboard">
          <div className="dashboard-content">
            {/* Project Add/Edit Form Modal */}
            {openProjectFormModal && (
              <div className="modalOverlay" onClick={() => { setOpenProjectFormModal(false); cancelEdit(); }}>
                <div className="modalContent form-modal-content" onClick={(e) => e.stopPropagation()}>
                  <button className="modalCloseButton" onClick={() => { setOpenProjectFormModal(false); cancelEdit(); }}>
                    &times;
                  </button>
                  <h2 className="modalTitle">{editingId ? 'Update Project' : 'Add Project'}</h2>
                  <form className="modalForm" onSubmit={handleSubmit}>
                    {[
                      { id: 'title', label: 'Project Title' },
                      { id: 'techStack', label: 'Tech Stack (comma-separated, e.g., React, Node.js)' },
                      { id: 'githubUrl', label: 'GitHub URL', type: 'url' },
                      { id: 'liveUrl', label: 'Live Project URL', type: 'url' },
                    ].map(({ id, label, type = 'text' }) => (
                      <div className="modalInputDiv" key={id}>
                        <label htmlFor={id} className="modalInputLabel">{label}</label>
                        <input
                          type={type}
                          id={id}
                          className="modalInput"
                          value={form[id]}
                          onChange={(e) => setForm({ ...form, [id]: e.target.value })}
                          required={id === 'title' || id === 'description' || (id === 'imageUrl' && form.imageUrl.filter(img => img.url.trim() !== '').length === 0)}
                        />
                      </div>
                    ))}

                    {/* Dynamic Image URL Inputs with Drag & Drop */}
                    <div className="modalInputDiv imageUrls-input-group">
                      <label className="modalInputLabel">Project Images (Drag to reorder)</label>
                      {form.imageUrl.map((img, index) => (
                        <div
                          key={index}
                          className={`image-url-input-row ${dragCurrentIndex.current === index ? 'dragging' : ''}`}
                          draggable="true"
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                        >
                          <span className="drag-handle" title="Drag to reorder">
                            <DragHandleIcon />
                          </span>
                          <div className="image-url-fields">
                            <input
                              type="url"
                              id={`image-input-${index}-url`} // Add ID for direct access
                              className="modalInput image-url-input"
                              placeholder={`Image URL ${index + 1}`}
                              value={img.url}
                              onChange={(e) => handleImageUrlChange(index, 'url', e.target.value)}
                              onFocus={() => handleFocus(index, 'url')}
                              onBlur={handleBlur}
                              required={form.imageUrl.filter(f => f.url.trim() !== '').length === 0 && index === 0}
                            />
                            <textarea
                              id={`image-input-${index}-description`} // Add ID for direct access
                              type="text"
                              className="modalInput image-description-input"
                              placeholder={`Description for Image ${index + 1}`}
                              value={img.description}
                              onChange={(e) => handleImageUrlChange(index, 'description', e.target.value)}
                              onFocus={() => handleFocus(index, 'description')}
                              onBlur={handleBlur}
                            />
                          </div>
                          {form.imageUrl.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveImageUrl(index)}
                              className="image-action-btn remove-url-btn"
                              title="Remove Image"
                            >
                              <RemoveCircleOutlineIcon fontSize="small" />
                            </button>
                          )}
                        </div>
                      ))}
                      <div className="image-url-controls-bottom"> {/* NEW: Container for global image buttons */}
                        <button
                          type="button"
                          onClick={handleAddImageUrl}
                          className="add-url-btn"
                          title="Add Another Image"
                        >
                          <AddCircleOutlineIcon /> Add Image
                        </button>
                        {/* NEW: Global Clear and Paste buttons */}
                        <button
                          type="button"
                          onClick={handleClearFocusedInput}
                          className={`image-action-btn clear-btn ${focusedImageInput.index === null ? 'disabled' : ''}`}
                          title="Clear Focused Input"
                          disabled={focusedImageInput.index === null}
                        >
                          <ClearIcon /> Clear
                        </button>
                        <button
                          type="button"
                          onClick={handlePasteIntoFocusedInput}
                          className={`image-action-btn paste-btn ${focusedImageInput.index === null ? 'disabled' : ''}`}
                          title="Paste into Focused Input"
                          disabled={focusedImageInput.index === null}
                        >
                          <ContentPasteIcon /> Paste
                        </button>
                      </div>
                    </div>

                    <div className="modalInputDiv">
                      <label className="modalInputLabel description-preview-label">Image Preview</label>
                      <div className="modalPreviewBox image-url-preview">
                        {form.imageUrl.filter(img => img.url.trim() !== '').length > 0 ?
                          form.imageUrl.filter(img => img.url.trim() !== '').map((img, idx) => (
                            <img key={idx} src={img.url.trim()} alt={img.description || `Preview ${idx + 1}`} className="preview-image" title={img.description || `Image ${idx + 1}`} />
                          ))
                          : 'Your images will appear here.'
                        }
                      </div>
                    </div>

                    <div className="modalInputDiv">
                      <label htmlFor="description" className="modalInputLabel">Project Description</label>
                      <textarea
                        id="description"
                        className="modalTextArea"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Type your project description..."
                        required
                      />
                      <label className="modalInputLabel description-preview-label">Description Preview</label>
                      <div className="modalPreviewBox description-preview-box">{form.description || 'Your description will appear here.'}</div>
                    </div>

                    <div className="modalButtonDiv">
                      <button className="modalButton modalSubmitButton" type="submit">
                        {editingId ? 'Update Project' : 'Add Project'}
                      </button>
                      <button
                        className="modalButton modalCancelButton"
                        type="button"
                        onClick={() => {
                          cancelEdit();
                          setOpenProjectFormModal(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="projectsContainer">
              {loading
                ? Array(itemsPerPage).fill().map((_, i) => <SkeletonLoader key={i} type="card" />)
                : displayedProjects.length > 0 ? displayedProjects.map((project) => (
                    <ProjectCard
                      key={project._id}
                      project={project}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      onViewDetails={(p) => { setSelectedProject(p); setCurrentContentIndex(0); }}
                    />
                  )) : (
                    <p className="no-projects-message">No projects found. Add one using the "+" button!</p>
                  )}
            </div>

            {/* Project View Modal with Carousel for entire content */}
            {selectedProject && (
              <div className="modalOverlay" onClick={() => { setSelectedProject(null); setCurrentContentIndex(0); }}>
                <div className="modalContent project-view-modal" onClick={(e) => e.stopPropagation()}>
                  <button className="modalCloseButton" onClick={() => { setSelectedProject(null); setCurrentContentIndex(0); }}>
                    &times;
                  </button>

                  {totalModalSlides > 1 && (
                    <div className="content-carousel-nav-buttons">
                      <button className="carousel-nav-btn left" onClick={handlePrevContent} aria-label="Previous content slide">
                        <ArrowBackIosNewIcon />
                      </button>
                      <button className="carousel-nav-btn right" onClick={handleNextContent} aria-label="Next content slide">
                        <ArrowForwardIosIcon />
                      </button>
                      <div className="carousel-content-counter">
                        {currentContentIndex + 1} / {totalModalSlides}
                      </div>
                    </div>
                  )}

                  <div className="modal-content-slide-wrapper">
                    {currentContentIndex === 0 && (
                      <div className="modal-slide main-description-slide">
                        <h3 className="project-modal-title">{selectedProject.title}</h3>
                        <p className="project-modal-description">{selectedProject.description}</p>
                        {selectedProject.techStack && selectedProject.techStack.length > 0 && (
                          <div className="project-modal-tech-stack">
                            <h4>Tech Stack:</h4>
                            <ul>
                              {selectedProject.techStack.map((tech, index) => (
                                <li key={index}>{tech}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="project-modal-links">
                          {selectedProject.githubUrl && (
                            <a href={selectedProject.githubUrl} target="_blank" rel="noopener noreferrer">
                              GitHub
                            </a>
                          )}
                          {selectedProject.liveUrl && (
                            <a href={selectedProject.liveUrl} target="_blank" rel="noopener noreferrer">
                              Live Demo
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedProject.imageUrl && selectedProject.imageUrl.map((img, index) => (
                      currentContentIndex === (index + 1) && (
                        <div className="modal-slide image-slide" key={index}>
                          {img.url ? (
                            <div className="project-modal-image-carousel">
                              <img
                                src={img.url}
                                alt={`${selectedProject.title} screenshot ${index + 1} - ${img.description}`}
                                className="project-modal-image"
                                onClick={() => setFullScreenImage(img)}
                              />
                              {img.description && (
                                <div className="carousel-image-description">
                                  {img.description}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="no-image-placeholder">No image available for this slide.</div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
              <div className="modalOverlay" onClick={() => setShowDeleteConfirm(false)}>
                <div className="modalAlertDiv" onClick={(e) => e.stopPropagation()}>
                  <button className="modalCloseButton" onClick={() => setShowDeleteConfirm(false)}>
                    &times;
                  </button>
                  <div className="modalTitle">Are you sure you want to delete?</div>
                  <strong>{projectToDelete?.title}</strong>
                  <div className="modalButtonDiv">
                    <button className="modalButton modalSubmitButton delete-confirm-button" onClick={confirmDelete}>
                      Delete
                    </button>
                    <button className="modalButton modalCancelButton" onClick={() => setShowDeleteConfirm(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Full-Screen Image Viewer Modal */}
            {fullScreenImage && (
              <div className="modalOverlay full-screen-image-overlay" onClick={() => setFullScreenImage(null)}>
                <div className="full-screen-image-container" onClick={(e) => e.stopPropagation()}>
                  <button className="modalCloseButton" onClick={() => setFullScreenImage(null)}>
                    &times;
                  </button>
                  <img src={fullScreenImage.url} alt="Full Screen" className="full-screen-image" />                  
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDashboard;